import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from 'lib/webStorage';
import { getCurrentUserRole } from 'selectors/app.selectors';
import {
    getOnboardingCurrentState,
    getRemoteOnBoardingState,
} from 'selectors/onboarding.selectors';
import eventTracking from 'services/analytics/eventTracking';
import { ApplicationMode } from 'services/enum';
import loggerService from 'services/logger';
import * as onBoardingApi from 'services/onboarding/api';

import { Thunk } from '../../types';
import { Nullable } from '../../types/general';
import { StoreState } from '../../types/store';
import { NewOnBoardingTrackingCategory, OnboardingState } from './types';

import addInfoPopupActionCreators from '../addInfoPopup/addInfoPopupActionCreators';

import {
    initializeOnBoardingCurrentState,
    initializeOnBoardingStateStack,
    onBoardingStateToCloseOnBoardingLabelMap,
    persistStateToStorageEffect,
    trackNewOnBoardingAction,
} from './utils';

import {
    INIT_ON_BOARDING_STEP_HISTORY,
    WS_KEY_CURRENT_ON_BOARDING_STEP,
    WS_KEY_FLOW_SHOWN,
    WS_KEY_HIDE_BANNER,
    WS_KEY_ON_BOARDING_STEP_HISTORY,
} from './constants';

export interface RemoteOnBoardingState {
    hasInvitedCoworker: boolean;
    hasInvitedRecipient: boolean;
    hasCheckedCompanies: boolean;
    hasUploadedDocument: boolean;
    hasSeenNewUploading: boolean;
    isSkipped: boolean;
}

interface InitialState {
    isShown: boolean;
    currentState: Nullable<OnboardingState>;
    stateStack: OnboardingState[];
    remoteState: RemoteOnBoardingState;
}

const initialState: InitialState = {
    isShown: false,
    currentState: INIT_ON_BOARDING_STEP_HISTORY[0],
    stateStack: INIT_ON_BOARDING_STEP_HISTORY,
    remoteState: {
        hasInvitedRecipient: true,
        hasCheckedCompanies: true,
        isSkipped: true,
        hasUploadedDocument: true,
        hasInvitedCoworker: true,
        hasSeenNewUploading: true,
    },
};

const persistState: InitialState = {
    ...initialState,
    currentState: initializeOnBoardingCurrentState(),
    stateStack: initializeOnBoardingStateStack(),
};

export const isRemoteOnboardingStateFinished = (
    remoteOnBoardingState: RemoteOnBoardingState,
): boolean => {
    return (
        remoteOnBoardingState.isSkipped ||
        (remoteOnBoardingState.hasInvitedCoworker &&
            remoteOnBoardingState.hasUploadedDocument &&
            remoteOnBoardingState.hasInvitedRecipient)
    );
};

export type StateType = InitialState;

const SET_VISIBLE_ACTION = 'ONBOARDING__SET_VISIBLE';
const SET_ONBOARDING_STATE_ACTION = 'ONBOARDING__SET_ONBOARDING_STATE';
const POP_ONBOARDING_STATE_ACTION = 'ONBOARDING__POP_ONBOARDING_STATE';
const RESET_ONBOARDING_ACTION = 'RESET_ONBOARDING_ACTION';
const SET_REMOTE_ONBOARDING = 'SET_REMOTE_ONBOARDING';

export const setVisible = (show: boolean) =>
    ({
        type: SET_VISIBLE_ACTION,
        visible: show,
    } as const);

export const setRemoteOnBoardingState = (
    payload: Partial<RemoteOnBoardingState>,
) =>
    ({
        type: SET_REMOTE_ONBOARDING,
        payload,
    } as const);

export const setOnboardingState = (state: StateType['currentState']) =>
    ({
        type: SET_ONBOARDING_STATE_ACTION,
        state,
    } as const);

export const popOnboardingState = () =>
    ({
        type: POP_ONBOARDING_STATE_ACTION,
    } as const);

export const resetOnboardingState = () =>
    ({
        type: RESET_ONBOARDING_ACTION,
    } as const);

type ActionType = ReturnType<
    | typeof setVisible
    | typeof setOnboardingState
    | typeof popOnboardingState
    | typeof resetOnboardingState
    | typeof setRemoteOnBoardingState
>;

export const fetchRemoteOnBoardingState = (): Thunk => async (dispatch) => {
    try {
        const onBoardingState = await onBoardingApi.getRemoteOnBoardingState();

        dispatch(setRemoteOnBoardingState(onBoardingState));
    } catch (error) {
        loggerService.error('Error during fetching user onboarding state', {
            err: error.message,
            stack: error.stack,
        });
    }
};

const generateMoveAction = (tag: OnboardingState): Thunk => (
    dispatch,
    getState,
) => {
    if (config.TEST) {
        return;
    }

    dispatch(setOnboardingState(tag));

    persistStateToStorageEffect(getState());
};

export const advanceOnboardingState = (): Thunk => async (
    dispatch,
    getState,
) => {
    const ONBOARDING_FINISHED_KEY = `${
        getCurrentUserRole(getState()).id
    }.${WS_KEY_FLOW_SHOWN}`;
    let isOnBoardingFinishedOnThisDevice = getLocalStorageItem(
        ONBOARDING_FINISHED_KEY,
    );

    if (!isOnBoardingFinishedOnThisDevice) {
        const onBoardingState = await onBoardingApi.getRemoteOnBoardingState();
        dispatch(setRemoteOnBoardingState(onBoardingState));

        isOnBoardingFinishedOnThisDevice = isRemoteOnboardingStateFinished(
            onBoardingState,
        );

        if (isOnBoardingFinishedOnThisDevice) {
            // prevent future request for this user device
            setLocalStorageItem(
                ONBOARDING_FINISHED_KEY,
                isOnBoardingFinishedOnThisDevice,
            );
        }
    }

    dispatch(setVisible(!isOnBoardingFinishedOnThisDevice));
};

export const conditionallyShowOnboarding = (): Thunk => (
    dispatch,
    getState,
) => {
    const {
        app: { applicationMode },
        onboarding: { isShown },
        router,
    } = getState();

    const isDocumentsList = router.location.pathname === '/app/documents';

    const skipOnboarding =
        applicationMode === ApplicationMode.SIGN_SESSION.value ||
        applicationMode === ApplicationMode.SHARED_DOCUMENT_VIEW.value ||
        isShown ||
        !isDocumentsList;

    if (!skipOnboarding) {
        dispatch(advanceOnboardingState());
    }
};

export const closeOnboarding = (): Thunk => (dispatch) => {
    dispatch(setVisible(false));
};

export const hideBanner = (): Thunk => async (dispatch, getState) => {
    const { id } = getCurrentUserRole(getState());
    const onboardingCurrentState = getOnboardingCurrentState(getState());
    const remoteOnBoardingState = getRemoteOnBoardingState(getState());

    setLocalStorageItem(`${id}.${WS_KEY_HIDE_BANNER}`, true);
    setLocalStorageItem(`${id}.${WS_KEY_FLOW_SHOWN}`, true);
    removeLocalStorageItem(WS_KEY_CURRENT_ON_BOARDING_STEP);
    removeLocalStorageItem(WS_KEY_ON_BOARDING_STEP_HISTORY);

    try {
        await onBoardingApi.updateRemoteOnBoardingSkip();
    } catch (error) {
        loggerService.error('Error during patch onboarding state', {
            err: error.message,
            stack: error.stack,
        });
    }

    if (
        onboardingCurrentState &&
        onBoardingStateToCloseOnBoardingLabelMap[onboardingCurrentState]
    ) {
        trackNewOnBoardingAction('close-onbording', {
            [onBoardingStateToCloseOnBoardingLabelMap[
                onboardingCurrentState
            ] as string]: true,
            'Invite-completed': remoteOnBoardingState.hasInvitedCoworker,
            'Upload-completed': remoteOnBoardingState.hasUploadedDocument,
            'Check-contractor-completed':
                remoteOnBoardingState.hasCheckedCompanies,
        });
    }

    return dispatch(closeOnboarding());
};

export const getCurrentOnboardingTrackingCategory = (
    _: StoreState,
): NewOnBoardingTrackingCategory => 'Onboarding-no-docs-new';

export const onSubmit = (fields: Record<string, string>): Thunk => async (
    dispatch,
    getState,
) => {
    if (fields.employeePostion) {
        eventTracking.sendEvent(
            getCurrentOnboardingTrackingCategory(getState()),
            'role',
            fields.employeePostion,
        );
        await dispatch(addInfoPopupActionCreators.onSubmit(fields));
    }
};

export const onMoveToUploadBanner = (): Thunk => (dispatch) => {
    trackNewOnBoardingAction('click-on-banner', 'upload-doc');
    dispatch(generateMoveAction('UPLOAD_WEB_BANNER'));
};

export const onMoveToInviteBanner = (): Thunk => (dispatch) => {
    trackNewOnBoardingAction('click-on-banner', 'invite-colleague');
    dispatch(generateMoveAction('INVITE_BANNER'));
};

export const onMoveToCheckUsersBanner = (): Thunk => (dispatch) => {
    trackNewOnBoardingAction('click-on-banner', 'check-contractor');
    dispatch(generateMoveAction('CHECK_USERS_BANNER'));
};

export const indicateVisibleOnMount = (tag?: OnboardingState): Thunk => (
    dispatch,
    getState,
) => {
    dispatch(setVisible(true));
    if (tag && getState().onboarding.currentState !== tag) {
        dispatch(setOnboardingState(tag));
    }
};

export const onGoBack = (): Thunk => (dispatch, getState) => {
    dispatch(popOnboardingState());

    persistStateToStorageEffect(getState());
};

export const onboardingReducer = (
    state: StateType = persistState,
    action: ActionType,
): StateType => {
    const prevState = state.stateStack[state.stateStack.length - 2];
    switch (action.type) {
        case SET_VISIBLE_ACTION:
            return {
                ...state,
                isShown: action.visible,
            };
        case SET_ONBOARDING_STATE_ACTION:
            return {
                ...state,
                currentState: action.state,
                stateStack: action.state
                    ? [...state.stateStack, action.state]
                    : state.stateStack,
                isShown: true,
            };
        case POP_ONBOARDING_STATE_ACTION:
            return {
                ...state,
                currentState: prevState ?? initialState.currentState,
                stateStack: state.stateStack
                    .slice(0, -2)
                    .concat(prevState ?? []),
            };
        case SET_REMOTE_ONBOARDING: {
            return {
                ...state,
                remoteState: {
                    ...state.remoteState,
                    ...action.payload,
                },
            };
        }
        case RESET_ONBOARDING_ACTION:
            return initialState;
        default:
            return state;
    }
};

export const shouldHideFooter = ({
    onboarding: { currentState, isShown },
}: StoreState) =>
    isShown &&
    ![
        'START_WORK',
        'INVITE_BANNER',
        'UPLOAD_WEB_BANNER',
        'CHECK_USERS_BANNER',
    ].includes(currentState ?? '');
